import type { AuditEventResponse, MemoryFigures, MemoryMetric } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { SettingsLevels, settingText } from "./SettingsAdmin.tsx";

/** The memory settings, in the order the screen shows them. */
const MEMORY_KEYS = ["memory.personal", "memory.project", "memory.retentionDays"] as const;

const METRICS: readonly MemoryMetric[] = [
  "personal_memories",
  "users_with_memories",
  "proposals_accepted",
  "proposals_dismissed",
  "project_memories",
  "pending_suggestions",
];

const EVENTS = 50;

/**
 * Memory administration (spec `tenant-administration` "Memory administration", openspec change
 * memory-governance): the three memory settings with their levels and locks, usage figures per
 * entity in the admin's scope (counts only; suppressed below 5 people by the server), and the
 * memory events. No route this screen calls returns what anyone saved.
 */
export function MemoryScreen() {
  const { t } = useTranslation();
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  return (
    <div data-esi-content="memory" className="flex flex-col gap-10">
      <PageHeader title={t("memoryAdmin.title")} lead={t("memoryAdmin.lead")} />
      <section aria-labelledby="memory-policies" className="flex flex-col gap-4">
        <h2 id="memory-policies" className="text-sm font-semibold text-fg">
          {t("memoryAdmin.policies")}
        </h2>
        <SettingsLevels keys={MEMORY_KEYS} />
      </section>
      <MemoryFiguresSection />
      {tenantAdmin ? <MemoryEvents /> : null}
    </div>
  );
}

type FigureRow = MemoryFigures["entities"][number] & { depth: number };

/** Parents before their children, siblings by name, with their depth for the indent. */
function treeOrder(entities: MemoryFigures["entities"]): FigureRow[] {
  const ids = new Set(entities.map((e) => e.entityId));
  const children = new Map<string | null, MemoryFigures["entities"]>();
  for (const e of entities) {
    const parent = e.parentId && ids.has(e.parentId) ? e.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), e]);
  }
  const rows: FigureRow[] = [];
  const visit = (parent: string | null, depth: number) => {
    for (const e of [...(children.get(parent) ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    )) {
      rows.push({ ...e, depth });
      visit(e.entityId, depth + 1);
    }
  };
  visit(null, 0);
  return rows;
}

function MemoryFiguresSection() {
  const { t, i18n } = useTranslation();
  const { admin } = useAdminApi();
  const figures = useQuery({
    queryKey: ["admin", "memory", "figures"],
    queryFn: async (): Promise<MemoryFigures> => ok(await admin.memory.$get()),
    retry: false,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const rows = figures.data ? treeOrder(figures.data.entities) : [];
  const shown = rows.filter((r) => r.figures !== null);
  const total = (metric: MemoryMetric) =>
    shown.reduce((sum, r) => sum + (r.figures?.[metric] ?? 0), 0);
  return (
    <section aria-labelledby="memory-figures" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id="memory-figures" className="text-sm font-semibold text-fg">
          {t("memoryAdmin.figures")}
        </h2>
        <p className="max-w-prose text-sm leading-relaxed text-fg-secondary">
          {t("memoryAdmin.figuresLead")}
        </p>
      </div>
      {figures.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : figures.isError ? (
        <Notice tone="danger">{figures.error.message}</Notice>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
          <table className="w-full min-w-[52rem] border-collapse text-left text-sm">
            <caption className="sr-only">{t("memoryAdmin.figures")}</caption>
            <thead className="border-b border-line text-xs text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("memoryAdmin.entity")}
                </th>
                {METRICS.map((m) => (
                  <th key={m} scope="col" className="px-4 py-2 text-right font-medium">
                    {t(`memoryAdmin.metrics.${m}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((row) => (
                <tr key={row.entityId}>
                  <th
                    scope="row"
                    className="min-w-40 px-4 py-2.5 font-normal text-fg"
                    style={{ paddingInlineStart: `${1 + row.depth * 1.25}rem` }}
                  >
                    {row.name}
                  </th>
                  {row.figures === null ? (
                    <td colSpan={METRICS.length} className="px-4 py-2.5 text-fg-muted">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <EyeOff aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                        {t("memoryAdmin.tooFew")}
                      </span>
                    </td>
                  ) : (
                    METRICS.map((m) => (
                      <td
                        key={m}
                        className="px-4 py-2.5 text-right whitespace-nowrap text-fg-secondary tabular-nums"
                      >
                        {number.format(row.figures?.[m] ?? 0)}
                      </td>
                    ))
                  )}
                </tr>
              ))}
            </tbody>
            {shown.length > 1 ? (
              <tfoot className="border-t border-line">
                <tr>
                  <th scope="row" className="px-4 py-2.5 font-medium text-fg">
                    {t("memoryAdmin.total")}
                  </th>
                  {METRICS.map((m) => (
                    <td
                      key={m}
                      className="px-4 py-2.5 text-right font-medium whitespace-nowrap text-fg tabular-nums"
                    >
                      {number.format(total(m))}
                    </td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The memory events: memory actions (expiries, exports, erasures, project memory reviews) and
 * changes to the three memory settings, latest first. Tenant admins only, as the audit log.
 */
function MemoryEvents() {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const events = useQuery({
    queryKey: ["admin", "audit", "memory"],
    queryFn: async (): Promise<AuditEventResponse[]> => {
      const limit = String(EVENTS);
      // Two queries: memory actions, and the three settings' changes (#84).
      const [actions, settings] = await Promise.all([
        admin.audit.$get({ query: { actionPrefix: "memory.", limit } }).then(ok),
        admin.audit
          .$get({ query: { targetType: "setting", targetIds: MEMORY_KEYS.join(","), limit } })
          .then(ok),
      ]);
      // A previous API release ignores `targetIds` and returns any setting's changes: then the
      // per-key queries, so other settings never crowd out the memory ones (canary N-1).
      const keys: readonly string[] = MEMORY_KEYS;
      const memorySettings = settings.every((e) => e.targetId && keys.includes(e.targetId))
        ? settings
        : (
            await Promise.all(
              MEMORY_KEYS.map((key) =>
                admin.audit
                  .$get({ query: { targetType: "setting", targetId: key, limit } })
                  .then(ok),
              ),
            )
          ).flat();
      return [...actions, ...memorySettings]
        .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
        .slice(0, EVENTS);
    },
    retry: false,
  });
  const forbidden = events.error instanceof ApiError && events.error.status === 403;
  const label = (action: string) =>
    t(`audit.actionLabel.${action.replaceAll(".", "_")}`, { defaultValue: action });
  return (
    <section aria-labelledby="memory-events" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id="memory-events" className="text-sm font-semibold text-fg">
          {t("memoryAdmin.activity")}
        </h2>
        <p className="max-w-prose text-sm leading-relaxed text-fg-secondary">
          {t("memoryAdmin.activityLead")}
        </p>
      </div>
      {events.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : forbidden ? (
        <p className="text-sm text-fg-muted">{t("memoryAdmin.activityTenantAdmins")}</p>
      ) : events.isError ? (
        <Notice tone="danger">{events.error.message}</Notice>
      ) : events.data.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-fg-muted">
          {t("memoryAdmin.activityEmpty")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
          <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
            <caption className="sr-only">{t("memoryAdmin.activity")}</caption>
            <thead className="border-b border-line text-xs text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("audit.when")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("audit.action")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("audit.actor")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {events.data.map((e) => (
                <tr key={String(e.id)}>
                  <td className="px-4 py-2.5 whitespace-nowrap text-fg-secondary tabular-nums">
                    {formatDateTime(e.occurredAt)}
                  </td>
                  <th scope="row" className="px-4 py-2.5 font-normal text-fg">
                    {label(e.action)}
                    {e.targetType === "setting" && e.targetId ? (
                      <span className="text-fg-muted"> · {settingText(e.targetId).label}</span>
                    ) : null}
                  </th>
                  <td className="px-4 py-2.5 whitespace-nowrap text-fg-secondary">
                    {t(`audit.actorType.${e.actorType}`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
